import type { LegalSection } from '../legalDocuments.constants'
import { WC2026_ORG_BILLING_TERMS_SECTION } from '../../../shared/constants/wc2026OrgBillingDisclosure.constants'

const ES_PRIVACY_POLICY_SECTIONS: readonly LegalSection[] = [
  {
    title: 'Introducción',
    paragraphs: [
      'Zporti («nosotros» o «nuestro») opera la aplicación móvil y web Zporti (el «Servicio»). Esta Política de privacidad describe cómo recopilamos, usamos y compartimos información cuando utilizas el Servicio.',
      'Al usar el Servicio, aceptas esta política. Si no estás de acuerdo, no utilices el Servicio.',
    ],
  },
  {
    title: 'Información que recopilamos',
    paragraphs: [
      'Información de cuenta y perfil: como nombre, dirección de correo electrónico, nombre de usuario, selección de avatar o imagen de perfil, y datos similares que proporciones al crear o actualizar tu cuenta.',
      'Datos de autenticación: usamos la autenticación de Supabase y podemos admitir inicio de sesión por correo electrónico, proveedores OAuth (por ejemplo, Google o Facebook) y sesiones de invitado según la plataforma y la versión.',
      'Contenido que creas: como predicciones deportivas, desafíos, membresías de grupos, conexiones con amigos, invitaciones y mensajes que envíes a través de formularios de contacto o soporte.',
      'Información de uso y del dispositivo: como eventos de la aplicación, ubicación aproximada derivada de la dirección IP cuando corresponda, tipo de dispositivo, sistema operativo y datos de diagnóstico para mantener el Servicio seguro y fiable.',
    ],
  },
  {
    title: 'Cookies, analítica y consentimiento',
    paragraphs: [
      'En la web, usamos cookies o almacenamiento esencial para la autenticación, la seguridad y el funcionamiento básico de la aplicación.',
      'La analítica no esencial está desactivada de forma predeterminada hasta que otorgues tu consentimiento. Puedes cambiar esta preferencia más adelante desde los controles de privacidad en la aplicación.',
      'Si está habilitada, los proveedores de analítica pueden procesar eventos de uso seudonimizados y metadatos limitados de la cuenta para ayudarnos a mejorar la calidad y la fiabilidad del producto.',
    ],
  },
  {
    title: 'Comunicaciones de marketing',
    paragraphs: [
      'Los correos de marketing son optativos. Solo enviamos comunicaciones de marketing cuando das tu consentimiento activamente.',
      'Puedes retirar el consentimiento de marketing en cualquier momento desde los controles de privacidad.',
      'Cuando creas una cuenta, registramos la fecha y la versión de la política para el consentimiento opcional de marketing y para tu aceptación de nuestros Términos de servicio y Política de privacidad, de modo que podamos demostrar lo que aceptaste si fuera necesario.',
    ],
  },
  {
    title: 'Cómo usamos la información',
    paragraphs: [
      'Usamos la información anterior para proporcionar y mejorar el Servicio, autenticar usuarios, personalizar tu experiencia, operar desafíos y clasificaciones, enviar avisos relacionados con el servicio, analizar el uso de forma agregada, prevenir fraude y abusos, y cumplir con la ley.',
    ],
  },
  {
    title: 'Cómo compartimos la información',
    paragraphs: [
      'Compartimos información con proveedores de servicios que nos ayudan a alojar el Servicio, enviar correos electrónicos, procesar analítica o realizar funciones similares, con las salvaguardas adecuadas.',
      'Podemos divulgar información si la ley lo exige, para proteger nuestros derechos y los de los usuarios, o en relación con una fusión o venta de activos (con aviso cuando sea necesario).',
      'Otros usuarios pueden ver la información que elijas hacer visible en la aplicación (por ejemplo, nombre para mostrar, avatar y actividad relacionada con desafíos) según las funciones del producto.',
    ],
  },
  {
    title: 'Conservación',
    paragraphs: [
      'Conservamos la información mientras tu cuenta esté activa y durante el tiempo necesario para prestar el Servicio, cumplir obligaciones legales, resolver disputas y hacer cumplir nuestros acuerdos.',
    ],
  },
  {
    title: 'Tus opciones y derechos',
    paragraphs: [
      'Según el lugar donde vivas, puedes tener derecho a acceder, corregir, eliminar o exportar tu información personal, u oponerte o restringir ciertos tratamientos.',
      'Los controles de privacidad en la aplicación incluyen solicitudes de eliminación de cuenta, controles de consentimiento de marketing y descarga de exportación de datos cuando esté disponible.',
      'También puedes contactarnos en info@zporti.com. Es posible que necesitemos verificar tu solicitud antes de responder.',
    ],
  },
  {
    title: 'Menores',
    paragraphs: [
      'El Servicio no está dirigido a menores de 13 años (o la edad mínima exigida en tu región). No recopilamos a sabiendas información personal de menores.',
    ],
  },
  {
    title: 'Cambios',
    paragraphs: [
      'Podemos actualizar esta Política de privacidad periódicamente. Publicaremos la versión actualizada en esta página y actualizaremos la fecha de vigencia cuando lo hagamos.',
    ],
  },
  {
    title: 'Contacto',
    paragraphs: [
      'Preguntas sobre esta política: info@zporti.com',
    ],
  },
]

const ES_DATA_DELETION_SECTIONS: readonly LegalSection[] = [
  {
    title: 'Resumen',
    paragraphs: [
      'Puedes solicitar la eliminación de los datos personales asociados a tu cuenta de Zporti. Esta página describe cómo hacer esa solicitud y qué puedes esperar.',
    ],
  },
  {
    title: 'Cómo solicitar la eliminación',
    paragraphs: [
      'Usa los controles de privacidad en la aplicación para solicitar la eliminación de la cuenta desde tu sesión autenticada.',
      'O envía un correo a info@zporti.com desde la dirección de correo asociada a tu cuenta de Zporti (o identifica claramente el correo de la cuenta y cualquier otro dato que podamos usar para localizar tu perfil).',
      'Usa el asunto «Data deletion request». Es posible que te pidamos confirmar tu identidad antes de procesar la solicitud.',
      'También puedes descargar una copia de los datos de tu cuenta desde los controles de privacidad en la aplicación.',
    ],
  },
  {
    title: 'Qué eliminamos',
    paragraphs: [
      'Tras la verificación, eliminaremos o anonimizaremos los datos personales vinculados a tu cuenta cuando la conservación no sea necesaria por motivos legales, de seguridad o de resolución de disputas.',
      'Parte de la información puede permanecer en copias de seguridad durante un periodo limitado hasta que se roten, o cuando la ley exija su conservación.',
    ],
  },
  {
    title: 'Plazos',
    paragraphs: [
      'Nos proponemos responder a las solicitudes verificadas en un plazo razonable y dentro de cualquier plazo exigido por la ley aplicable en tu región.',
    ],
  },
  {
    title: 'Contacto',
    paragraphs: [
      'Preguntas sobre eliminación de datos: info@zporti.com',
    ],
  },
]

const ES_TERMS_OF_SERVICE_SECTIONS: readonly LegalSection[] = [
  {
    title: 'Acuerdo',
    paragraphs: [
      'Estos Términos de servicio («Términos») rigen tu acceso y uso de la aplicación móvil y web Zporti (el «Servicio») operada por Zporti («nosotros» o «nuestro»).',
      'Al crear una cuenta, acceder o usar el Servicio, aceptas estos Términos. Si no estás de acuerdo, no utilices el Servicio.',
      'Al registrarte, confirmas la aceptación de estos Términos y de nuestra Política de privacidad por separado de cualquier consentimiento opcional para correos de marketing.',
    ],
  },
  {
    title: 'El Servicio',
    paragraphs: [
      'Zporti ofrece una experiencia social de predicciones y desafíos deportivos: puedes crear o unirte a desafíos con amigos, enviar predicciones, ver resultados y clasificaciones, y usar funciones relacionadas que podamos añadir o modificar con el tiempo.',
      'Podemos modificar, suspender o discontinuar partes del Servicio con un aviso razonable cuando sea posible. No garantizamos un funcionamiento ininterrumpido o libre de errores.',
    ],
  },
  {
    title: 'Elegibilidad y cuentas',
    paragraphs: [
      'Debes cumplir la edad mínima exigida en tu región para usar el Servicio (normalmente al menos 13 años, o más si la ley local lo requiere).',
      'Eres responsable de la exactitud de la información que proporcionas y de mantener seguras tus credenciales de acceso. Notifícanos de inmediato en el contacto indicado abajo si sospechas un acceso no autorizado a tu cuenta.',
    ],
  },
  {
    title: 'Uso aceptable',
    paragraphs: [
      'Aceptas no hacer un uso indebido del Servicio. Por ejemplo, no debes: infringir la ley; acosar, abusar o dañar a otros; intentar obtener acceso no autorizado a nuestros sistemas o a las cuentas de otros usuarios; extraer datos o sobrecargar el Servicio; distribuir malware; hacer trampa, manipular o alterar desafíos o resultados; ni usar el Servicio para enviar spam o comunicaciones engañosas.',
      'Podemos investigar y suspender o cerrar cuentas que infrinjan estos Términos o pongan en riesgo el Servicio o a los usuarios.',
    ],
  },
  {
    title: 'Contenido del usuario',
    paragraphs: [
      'Conservas los derechos sobre el contenido que envías (como información de perfil, mensajes y actividad en desafíos). Nos concedes una licencia no exclusiva para alojar, almacenar, mostrar y procesar ese contenido según sea necesario para operar y mejorar el Servicio.',
      'Eres responsable del contenido que envías y confirmas que tienes derecho a compartirlo. Podemos eliminar contenido que infrinja estos Términos o la ley aplicable.',
    ],
  },
  {
    title: WC2026_ORG_BILLING_TERMS_SECTION.title,
    paragraphs: [...WC2026_ORG_BILLING_TERMS_SECTION.paragraphs],
  },
  {
    title: 'Exenciones de responsabilidad',
    paragraphs: [
      'El Servicio se proporciona «tal cual» y «según disponibilidad» en la máxima medida permitida por la ley. Los datos deportivos, cuotas y resultados pueden proceder de terceros; no garantizamos su exactitud ni actualidad.',
      'La competición amistosa y las predicciones son con fines de entretenimiento. Nada en el Servicio constituye asesoramiento sobre apuestas, finanzas o profesional cuando dicha actividad esté regulada o prohibida.',
    ],
  },
  {
    title: 'Limitación de responsabilidad',
    paragraphs: [
      'En la máxima medida permitida por la ley aplicable, no somos responsables de daños indirectos, incidentales, especiales, consecuentes o punitivos, ni de la pérdida de beneficios, datos o fondo de comercio, derivados de tu uso del Servicio.',
      'Nuestra responsabilidad total por reclamaciones derivadas del Servicio se limita al mayor de (a) el importe que nos hayas pagado por el Servicio en los doce meses anteriores a la reclamación o (b) cien dólares estadounidenses (USD $100), salvo cuando la ley no permita dicho límite.',
    ],
  },
  {
    title: 'Indemnización',
    paragraphs: [
      'Aceptas defendernos e indemnizarnos a nosotros y a nuestras filiales, directivos y empleados frente a reclamaciones, daños y gastos (incluidos honorarios legales razonables) derivados de tu uso del Servicio, tu contenido o tu incumplimiento de estos Términos, en la medida permitida por la ley.',
    ],
  },
  {
    title: 'Ley aplicable y disputas',
    paragraphs: [
      'Estos Términos se rigen por las leyes aplicables en el lugar donde Zporti opera su negocio, sin tener en cuenta normas sobre conflictos de leyes, salvo cuando las protecciones obligatorias al consumidor en tu país dispongan lo contrario.',
      'Si eres consumidor, puedes tener derecho a plantear disputas ante los tribunales de tu lugar de residencia cuando la ley lo exija.',
    ],
  },
  {
    title: 'Cambios',
    paragraphs: [
      'Podemos actualizar estos Términos periódicamente. Publicaremos los Términos actualizados en esta página. El uso continuado del Servicio después de que los cambios entren en vigor constituye la aceptación de los Términos revisados cuando la ley lo permita.',
    ],
  },
  {
    title: 'Contacto',
    paragraphs: [
      'Preguntas sobre estos Términos: info@zporti.com',
    ],
  },
]

const ES_CREDITS_SECTIONS: readonly LegalSection[] = [
  {
    title: 'Créditos de terceros',
    paragraphs: [
      'Zporti utiliza recursos de audio de terceros seleccionados para efectos de sonido en la aplicación.',
      'Los recursos de sonido proceden de Freesound y se usan bajo licencia CC0 (dominio público).',
    ],
  },
  {
    title: 'Atribución de audio',
    paragraphs: [
      'Freesound: https://freesound.org/',
      'Creative Commons CC0 1.0: https://creativecommons.org/publicdomain/zero/1.0/',
    ],
  },
]

export const ES_LEGAL_SECTIONS = {
  privacy: ES_PRIVACY_POLICY_SECTIONS,
  dataDeletion: ES_DATA_DELETION_SECTIONS,
  terms: ES_TERMS_OF_SERVICE_SECTIONS,
  credits: ES_CREDITS_SECTIONS,
} as const
